import {useMemo} from 'react';

import {useLocalesMgr, useProduct} from 'coreHooks';
import {getIsInternalUrl} from 'coreUtils/url';
import {useCategory} from 'coreHooks/categories';

/**
 * Resolves the URL for a Contentful entry target in rich text.
 * Handles: CTA (internal/external), Page (slug + locale), Category (async), URI fallback, Product (async).
 * Replaces TextSubItemVisitor -> NavigationItemVisitor/BrandVisitor chain.
 *
 * @param target - The `data.target` from a rich-text entry-hyperlink or embedded-entry node.
 * @param fallbackUri - Optional `data.uri` to use as last resort.
 * @returns Resolved URL string, or undefined while pending.
 */
const useResolvedEntryUrl = (target: any, fallbackUri?: string): string | undefined => {
	const localesMgr = useLocalesMgr();
	const categoryId = (() => {
		switch (true) {
			case Boolean(target?.targetCategory):
				return target.targetCategory;
			case Boolean(target?.category):
				return target.category;
			case Boolean(target?.category?.items?.[0]?.id):
				return target.category.items[0].id;
			case target?.sys?.linkType === 'category':
				return target?.sys?.urn?.split(':')?.pop();
			default:
				return null;
		}
	})();
	const category = useCategory(categoryId);
	const categoryUrl = category?.getCategoryUrl();
	const productId = (() => {
		switch (true) {
			case !category && !fallbackUri && target?.sys?.linkType === 'product':
				return target?.sys?.urn?.split(':')?.pop();
			// CTA "Target Product": lowest priority, after targetUrl and category
			case !categoryId && !fallbackUri && !target?.targetUrl:
				return target?.targetProduct?.items?.[0]?.id || null;
			default:
				return null;
		}
	})();

	const productUrl = useProduct(productId)?.getProductUrl();

	return useMemo(() => {
		if (target?.__typename === 'Cta' && target?.targetUrl) {
			const u = target.targetUrl;

			return getIsInternalUrl(u) ? localesMgr.updateLinkWithLocale(u) : u;
		} else if (target?.__typename === 'Page' && target?.slug) {
			return localesMgr.updateLinkWithLocale(target.slug);
		} else if (categoryUrl) {
			return categoryUrl;
		} else if (fallbackUri) {
			return fallbackUri;
		} else if (productUrl) {
			return productUrl;
		}
	}, [categoryUrl, target, localesMgr, fallbackUri, productUrl]);
};

export default useResolvedEntryUrl;
